import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './HelpPanel.css';

// Recursively extract plain text from JSX elements for search indexing
function extractText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (node.props) {
    const { children } = node.props;
    return extractText(children);
  }
  return '';
}

const sections = [
  /* ───── 1. Erste Schritte ───── */
  {
    id: 'getting-started',
    icon: 'bi-rocket-takeoff',
    title: 'Erste Schritte',
    content: (
      <>
        <p>
          <strong>Neuer Prozess:</strong> Klicken Sie auf <strong>Neu</strong> in der Werkzeugleiste
          und vergeben Sie einen Prozessnamen. Es wird ein leeres Diagramm mit einem Start-Event erstellt.
        </p>
        <p>
          <strong>Prozess laden:</strong> Klicken Sie auf <strong>Laden</strong> und wählen Sie
          als Quelle <strong>SharePoint</strong> oder <strong>Lokale Datei</strong>.
          Geladen werden <code>.bpmn</code>-Dateien und Signavio-Archive (<code>.sgx</code>).
          Falls die Datei <strong>keine visuelle Layoutinformation</strong> (DI) enthält,
          wird automatisch ein Layout generiert, sodass alle Elemente sichtbar positioniert werden.
        </p>
        <p>
          <strong>Signavio-Archiv (<code>.sgx</code>) öffnen:</strong> Ein Archiv wird beim Laden
          automatisch nach BPMN konvertiert — an beiden Quellen gleichermaßen. Enthält es mehrere
          Prozesse, erscheint eine Auswahlliste (nach Kunde und Prozessname filterbar);
          bei einem einzigen Prozess wird dieser direkt geöffnet. Große Archive mit mehreren
          hundert Prozessen brauchen dafür bis zu einer Minute; währenddessen zeigt der Dialog
          den Fortschritt. Der konvertierte Prozess ist ein
          <strong> neues, noch ungespeichertes Dokument</strong>: Er muss über
          <strong> Speichern</strong> bzw. <strong>Speichern unter…</strong> abgelegt werden —
          das Archiv selbst wird nie überschrieben.
        </p>
        <p>
          <strong>Signavio-Export als <code>.bpmn</code>:</strong> Auch eine einzelne, aus Signavio
          exportierte BPMN-Datei wird beim Laden automatisch bereinigt — Signavio-eigene Zusätze,
          Beschriftungspositionen und Revisionsdaten werden entfernt, die Diagrammstruktur bleibt
          unangetastet. Unten erscheint kurz die Meldung <em>„Signavio-Import bereinigt"</em> mit
          dem, was geschehen ist. <strong>IT-Systeme</strong> aus Signavio haben kein BPMN-Gegenstück
          und werden als Text-Annotation mit <strong>Computer-Symbol</strong> dargestellt.
          Auch hier gilt: bereinigt ist noch nicht gespeichert.
        </p>
        <p>
          <strong>Speichern:</strong> Beim ersten Speichern fragt der Editor nach dem Ziel:
          <strong> In SharePoint</strong> oder <strong>Lokal speichern</strong>. Danach schreibt
          <strong> Speichern</strong> ohne weitere Rückfrage an dieselbe Stelle. Ein SharePoint-Dokument
          landet unter <code>Kundenprojekte/</code>, eine lokale Datei dort, wo Sie sie ablegen.
        </p>
        <p>
          <strong>Speichern unter:</strong> Klicken Sie auf den <strong>kleinen Pfeil ▾</strong>{' '}
          rechts neben dem Speichern-Button, um <strong>Speichern unter…</strong> zu wählen.
          Damit können Sie den Prozess unter einem neuen Namen und/oder an einem neuen Speicherort ablegen.
        </p>
        <p>
          <strong>Ungespeicherte Änderungen:</strong> Wenn Sie nicht gespeicherte Änderungen haben,
          zeigt der Editor ein <strong>●</strong>-Symbol am Speichern-Button.
          Beim Laden einer neuen Datei oder beim Schließen des Browsertabs erscheint eine
          Sicherheitsabfrage, ob Sie die Änderungen speichern, verwerfen oder abbrechen möchten.
        </p>
        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Der Browsertab zeigt den aktuellen Prozessnamen an.
        </div>
      </>
    ),
  },

  /* ───── 2. BPMN-Elemente (Übersicht) ───── */
  {
    id: 'bpmn-elements',
    icon: 'bi-diagram-3',
    title: 'BPMN-Elemente (Übersicht)',
    content: (
      <>
        <p>
          Am linken Rand des Editors befindet sich die <strong>Element-Palette</strong>.
          Per Drag &amp; Drop ziehen Sie Elemente auf die Zeichenfläche.
        </p>

        <div className="help-tip">
          <i className="bi bi-info-circle"></i>{' '}
          Die Palette führt bewusst nur die <strong>Grundtypen</strong>: Start-, Zwischen- und
          End-Ereignis, Gateway, Task, aufgeklappter Sub-Prozess, Datenobjekt, Datenspeicher,
          Pool und Gruppe — dazu oben die Werkzeuge Hand, Lasso, Abstand und Verbinden.
          Die <strong>Spielarten</strong> (User Task, Timer-Event, Parallel-Gateway …) entstehen
          nicht durch Ziehen, sondern indem Sie ein gesetztes Element auswählen und über das
          <strong> Schraubenschlüssel-Symbol</strong> den Typ ändern. Wer einen User Task in der
          Palette sucht, sucht vergebens.
        </div>

        <p><strong>Events (Kreise):</strong></p>
        <ul>
          <li><strong>Start-Event</strong> – dünner Kreis, Beginn eines Prozesses</li>
          <li><strong>Zwischen-Event (Intermediate)</strong> – doppelter Kreis, z.&nbsp;B. Timer, Nachricht, Signal</li>
          <li><strong>End-Event</strong> – dicker Kreis, Prozessende</li>
        </ul>

        <p><strong>Tasks (abgerundete Rechtecke):</strong></p>
        <ul>
          <li><strong>Task</strong> – generische Aufgabe (kein Typ-Symbol)</li>
          <li><strong>User Task</strong> – wird von einem Benutzer ausgeführt (Personen-Symbol)</li>
          <li><strong>Service Task</strong> – automatisierte Systemaufgabe (Zahnrad-Symbol)</li>
          <li><strong>Script Task</strong> – Skriptausführung (Blatt-Symbol)</li>
          <li><strong>Send / Receive Task</strong> – sendet / empfängt Nachrichten</li>
          <li><strong>Business Rule Task</strong> – Geschäftsregelauswertung (Tabellen-Symbol)</li>
          <li><strong>Manual Task</strong> – manuell ohne IT-Unterstützung (Hand-Symbol)</li>
          <li><strong>Call Activity</strong> – Aufruf eines externen Prozesses (fetter Rand)</li>
        </ul>

        <p><strong>Gateways (Rauten):</strong></p>
        <ul>
          <li><strong>Exclusive (XOR)</strong> – genau ein Pfad wird gewählt (✕-Symbol)</li>
          <li><strong>Parallel (AND)</strong> – alle Pfade gleichzeitig (+-Symbol)</li>
          <li><strong>Inclusive (OR)</strong> – ein oder mehrere Pfade (○-Symbol)</li>
          <li><strong>Event-based</strong> – Entscheidung durch eingehendes Event</li>
        </ul>

        <p><strong>Weitere Elemente:</strong></p>
        <ul>
          <li><strong>Sub-Prozess</strong> – zusammengefasster Unterprozess (siehe Abschnitt „Sub-Prozesse")</li>
          <li><strong>Pool &amp; Lanes</strong> – Teilnehmer &amp; Verantwortlichkeiten (siehe Abschnitt „Pools &amp; Lanes")</li>
          <li><strong>Datenobjekte / Datenspeicher</strong> – Datenflüsse</li>
          <li><strong>Gruppe</strong> – rahmt zusammengehörige Elemente ein, ohne den Ablauf
              zu verändern; die Beschriftung steht am oberen Rand</li>
          <li><strong>Textannotation</strong> – Kommentare im Diagramm. Sie liegt <em>nicht</em>
              in der Palette: Element auswählen und im Kontextmenü
              <strong> „Add text annotation"</strong> wählen — die Annotation hängt dann am Element.</li>
        </ul>

        <p>
          Um Elemente zu <strong>verbinden</strong>, klicken Sie auf ein Element und ziehen den
          erscheinenden Pfeil zum Zielelement.
        </p>
        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Doppelklick auf ein Element öffnet die Inline-Bearbeitung des Namens.
        </div>
      </>
    ),
  },

  /* ───── 3. Element bearbeiten & Typ ändern ───── */
  {
    id: 'context-pad',
    icon: 'bi-wrench',
    title: 'Element bearbeiten & Typ ändern',
    content: (
      <>
        <p>
          Wenn Sie ein Element <strong>anklicken</strong>, erscheinen kleine Icons
          rund um das Element — das <strong>Kontextmenü</strong> (Context Pad):
        </p>
        <ul>
          <li>
            <strong>Schraubenschlüssel-Symbol:</strong> Öffnet das
            {' '}<strong>Typ-Ändern-Menü</strong> (Replace). Hier können Sie z.&nbsp;B.:
            <ul>
              <li>Einen <em>Task</em> in einen <em>User Task</em>, <em>Service Task</em>,{' '}
                  <em>Script Task</em>, <em>Send Task</em>, <em>Business Rule Task</em> etc. umwandeln</li>
              <li>Ein <em>Start-Event</em> in ein <em>Timer-</em>, <em>Message-</em>,{' '}
                  <em>Signal-</em> oder <em>Conditional-Start-Event</em> ändern</li>
              <li>Ein <em>Gateway</em> zwischen Exclusive, Parallel, Inclusive, Event-based und Complex wechseln</li>
              <li>Einen <em>Task</em> in einen <em>Sub-Prozess</em> oder eine <em>Call Activity</em> umwandeln</li>
            </ul>
          </li>
          <li>
            <strong>Pfeil-Symbole (Append):</strong> Direkt ein neues verbundenes Element
            anhängen — Task, Gateway, Intermediate Event oder End Event.
          </li>
          <li>
            <strong>Papierkorb-Symbol:</strong> Element löschen (alternativ: <kbd>Delete</kbd>-Taste)
          </li>
          <li>
            <strong>Farbpaletten-Symbol:</strong> Farbe setzen oder zurücksetzen
            (siehe Abschnitt „Farben ändern")
          </li>
          <li>
            <strong>Notiz-Symbol:</strong> Textannotation an das Element hängen
          </li>
          <li>
            <strong>Verbinden-Symbol:</strong> eine Verbindung zu einem anderen Element ziehen
          </li>
          <li>
            An einem <strong>Ende-Ereignis</strong> kommt „Als BRE auslösen" hinzu, an einem
            <strong> Sub-Prozess</strong> das Auf- und Zuklappen — beides in eigenen Abschnitten.
          </li>
        </ul>
        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Sie können den Typ eines Elements jederzeit über den Schraubenschlüssel ändern —
          bestehende Verbindungen bleiben erhalten!
        </div>
      </>
    ),
  },

  /* ───── 4. Farben ändern ───── */
  {
    id: 'colors',
    icon: 'bi-palette',
    title: 'Farben ändern',
    content: (
      <>
        <p>
          Der Editor enthält einen integrierten <strong>Color Picker</strong>,
          mit dem Sie die Farbe von Elementen ändern können:
        </p>
        <ol>
          <li>Klicken Sie auf ein Element (oder markieren Sie mehrere mit <kbd>Ctrl</kbd> + Klick).</li>
          <li>
            Klicken Sie im <strong>Kontextmenü</strong> (Context Pad) auf das
            {' '}<strong>Farbpaletten-Symbol</strong>.
          </li>
          <li>
            Wählen Sie eine Farbe aus der Palette — Rahmen- und Füllfarbe
            werden automatisch angepasst.
          </li>
        </ol>
        <p><strong>Verfügbare Optionen:</strong></p>
        <ul>
          <li>Standardfarben: Blau, Grün, Gelb, Orange, Rot, Lila u.&nbsp;a.</li>
          <li><strong>Zurücksetzen:</strong> Wählen Sie die Standard-Option, um die
              Farbe auf den Originalzustand zurückzusetzen.</li>
        </ul>

        <p><strong>Automatische Standardfarben:</strong></p>
        <ul>
          <li>Neu erstellte Elemente erhalten automatisch eine <strong>Standardfarbe</strong>:
            <ul>
              <li><strong>Tasks</strong> → Orange</li>
              <li><strong>Datenobjekte</strong> → Gelb</li>
              <li><strong>Eingeklappte Sub-Prozesse</strong> → Blau</li>
            </ul>
          </li>
          <li>Beim <strong>Öffnen einer Datei</strong> werden Standardfarben automatisch zugewiesen,
              sofern <strong>kein einziges Element</strong> im Diagramm bereits eine Farbe besitzt.
              Dateien, die bereits farblich gestaltet sind, bleiben unverändert.</li>
          <li>Beim <strong>Import als Sub-Prozess</strong> (siehe Abschnitt „Sub-Prozesse")
              werden die importierten Elemente ebenfalls automatisch eingefärbt,
              wenn die Quelldatei keine Farben enthielt.</li>
          <li>Manuell gesetzte Farben werden nicht überschrieben.</li>
          <li>Die automatische Farbzuweisung kann über den Color Picker zurückgesetzt werden.</li>
        </ul>
        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Farben werden in der BPMN-Datei gespeichert und erscheinen auch im PDF-Export sowie
          beim erneuten Laden.
        </div>
      </>
    ),
  },

  /* ───── 5. Sub-Prozesse ───── */
  {
    id: 'subprocesses',
    icon: 'bi-bounding-box',
    title: 'Sub-Prozesse',
    content: (
      <>
        <p>
          Sub-Prozesse fassen mehrere Schritte in einem übergeordneten Container zusammen
          und machen komplexe Abläufe übersichtlicher.
        </p>

        <p><strong>Sub-Prozess erstellen:</strong></p>
        <ol>
          <li>
            Ziehen Sie einen <strong>Sub-Prozess</strong> aus der Palette auf die Zeichenfläche,
            {' '}<em>oder</em> wandeln Sie einen bestehenden Task über den
            Schraubenschlüssel (Replace-Menü) in einen Sub-Prozess um.
          </li>
          <li>
            Der Sub-Prozess wird zunächst <strong>eingeklappt</strong> angezeigt
            (kleines <strong>[+]</strong>-Symbol unten).
          </li>
        </ol>

        <p><strong>Sub-Prozess aus Datei importieren:</strong></p>
        <ol>
          <li>
            Klicken Sie auf einen bestehenden Task und öffnen Sie das
            {' '}<strong>Replace-Menü</strong> (Schraubenschlüssel-Symbol).
          </li>
          <li>
            Wählen Sie <strong>„Sub-process (from file)"</strong> — der Eintrag
            befindet sich zwischen <em>Sub-process (collapsed)</em> und{' '}
            <em>Sub-process (expanded)</em>.
          </li>
          <li>
            Wählen Sie eine <code>.bpmn</code>- oder <code>.xml</code>-Datei von Ihrem Computer.
            Der Task wird durch einen <strong>eingeklappten Sub-Prozess</strong> ersetzt,
            dessen Name dem Prozessnamen der importierten Datei entspricht.
          </li>
          <li>
            Alle <strong>Elemente und Verbindungen</strong> aus der importierten Datei werden
            in den Sub-Prozess übernommen — inklusive verschachtelter Sub-Prozesse.
          </li>
        </ol>
        <ul>
          <li>Falls die importierte Datei <strong>kein visuelles Layout</strong> (DI) enthält,
              wird automatisch ein Layout generiert.</li>
          <li>Falls die importierte Datei <strong>keine Farben</strong> enthält,
              werden die Standardfarben automatisch zugewiesen.</li>
          <li>Eine eventuell in der Datei vorhandene <strong>Versionshistorie</strong> wird
              {' '}<strong>nicht</strong> mit importiert — nur die Prozesselemente werden übernommen.</li>
        </ul>

        <p><strong>Aufklappen / Einklappen:</strong></p>
        <ul>
          <li>
            Klicken Sie auf das <strong>[+]</strong>-Symbol am unteren Rand, um den Sub-Prozess
            {' '}<strong>aufzuklappen</strong> und den Inhalt zu bearbeiten.
          </li>
          <li>
            Im aufgeklappten Zustand können Sie innerhalb des Sub-Prozesses einen
            eigenen Ablauf anlegen: Start-Event → Tasks → End-Event.
          </li>
          <li>
            Klicken Sie auf das <strong>[−]</strong>-Symbol, um wieder einzuklappen.
          </li>
        </ul>

        <p><strong>Spezialtypen (über Schraubenschlüssel / Replace-Menü):</strong></p>
        <ul>
          <li><strong>Embedded Sub-Process</strong> – Standard-Unterprozess, eingebettet im Elternprozess</li>
          <li><strong>Event Sub-Process</strong> – wird durch ein bestimmtes Event ausgelöst (gestrichelter Rand).
              Setzen Sie den Start-Event-Typ (Message, Error, Timer etc.).</li>
          <li><strong>Transaction</strong> – atomare Geschäftstransaktion mit Kompensations-Handling (doppelter Rand)</li>
          <li><strong>Ad-hoc Sub-Process</strong> – Aktivitäten ohne feste Reihenfolge (~-Symbol)</li>
        </ul>

        <p><strong>Sub-Prozess-Navigation (Baumansicht):</strong></p>
        <ul>
          <li>Wenn das Diagramm Sub-Prozesse enthält, erscheint im <strong>Eigenschaften-Panel</strong>{' '}
              eine eigene Gruppe <strong>„Sub-Prozesse"</strong> mit einer hierarchischen Baumansicht.</li>
          <li>Klicken Sie auf einen Eintrag im Baum, um direkt zur Ebene dieses Sub-Prozesses zu navigieren.</li>
          <li>Ein Button <strong>„Zur Hauptebene"</strong> bringt Sie jederzeit zurück zur obersten Ebene.</li>
          <li>Verschachtelte Sub-Prozesse werden eingerückt dargestellt, sodass die Hierarchie sofort erkennbar ist.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Eingeklappte Sub-Prozesse werden im PDF-Export automatisch auf eigenen
          Seiten als vollständige BPMN-Diagramme dargestellt.
        </div>
      </>
    ),
  },

  /* ───── 5b. In Sub-Prozess umwandeln ───── */
  {
    id: 'wrap-subprocess',
    icon: 'bi-box-arrow-in-down',
    title: 'In Sub-Prozess umwandeln',
    content: (
      <>
        <p>
          Sie können mehrere bestehende Elemente auswählen und gemeinsam in einen
          neuen Sub-Prozess verpacken.
        </p>

        <p><strong>Schritt für Schritt:</strong></p>
        <ol>
          <li>
            Wählen Sie mindestens <strong>2 Elemente</strong> aus (<kbd>Ctrl</kbd>{' '}+{' '}Klick
            oder Lasso-Auswahl).
          </li>
          <li>
            Klicken Sie auf den blauen Button <strong>„In Sub-Prozess"</strong>, der unter der
            Auswahl erscheint, oder drücken Sie <kbd>Ctrl</kbd>{' '}+{' '}<kbd>G</kbd>.
          </li>
          <li>
            Im Dialog wählen Sie <strong>Zugeklappt</strong> (kompakt) oder{' '}
            <strong>Aufgeklappt</strong> (Elemente bleiben sichtbar) und vergeben optional
            einen Namen.
          </li>
        </ol>

        <p><strong>Verbindungen:</strong></p>
        <ul>
          <li>In beiden Varianten werden automatisch ein{' '}
              <strong>Start- und End-Event</strong> im Sub-Prozess erzeugt und mit den
              ein-/ausgehenden Elementen verbunden.</li>
          <li>Verbindungen <strong>zwischen</strong> den ausgewählten Elementen bleiben erhalten.</li>
          <li>Verbindungen <strong>von/zu externen Elementen</strong> werden automatisch auf den
              neuen Sub-Prozess umgeleitet.</li>
        </ul>

        <p><strong>Konsistenzprüfung:</strong></p>
        <p>
          Vor dem Umwandeln prüft der Editor, ob Elemente <em>außerhalb</em> der Auswahl
          ausschließlich mit ausgewählten Elementen verbunden sind. Solche Elemente würden
          nach dem Wrapping isoliert „in der Luft hängen". In diesem Fall zeigt der Dialog
          eine <strong>Warnung</strong> mit den betroffenen Elementnamen, damit Sie Ihre Auswahl
          ggf. anpassen können.
        </p>

        <p><strong>Zuklappen / Aufklappen umschalten:</strong></p>
        <ul>
          <li>Klicken Sie auf einen Sub-Prozess und nutzen Sie den{' '}
              <strong>Zuklappen/Aufklappen-Button</strong> im Context-Pad, um nachträglich
              zwischen den Darstellungen zu wechseln.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Die Konsistenzprüfung ist nur eine Warnung — Sie können die Umwandlung
          trotzdem durchführen, wenn isolierte Elemente beabsichtigt sind.
        </div>
      </>
    ),
  },

  /* ───── 5b. Bausteine ───── */
  {
    id: 'bausteine',
    icon: 'bi-box-seam',
    title: 'Bausteine (wiederverwendbare Sub-Prozesse)',
    content: (
      <>
        <p>
          Ein <strong>Baustein</strong> ist ein Sub-Prozess, der an mehreren Stellen
          derselben Datei verwendet werden kann. Er liegt als eigener Prozess in der
          Datei — nicht in einer Bibliothek —, die Datei bleibt also für sich vollständig.
        </p>

        <p><strong>Baustein anlegen:</strong></p>
        <ul>
          <li>Einen eingeklappten Sub-Prozess <strong>markieren</strong>. In der kleinen
            Werkzeugleiste, die daneben erscheint, auf das <strong>Würfel-Symbol</strong>{' '}
            (<i className="bi bi-box-seam"></i>, Tooltip <em>„Als Baustein herauslösen"</em>)
            klicken. Der Inhalt wandert in den Baustein, an der bisherigen Stelle bleibt ein
            Aufruf zurück.</li>
          <li>Denselben Eintrag gibt es auch im <strong>Schraubenschlüssel-Menü</strong>. Dort
            steht er allerdings weit unten: Die Liste hat bei einem Sub-Prozess sechzehn
            Einträge, von denen ohne Scrollen nur acht zu sehen sind. Über das Suchfeld am
            oberen Rand des Menüs ist er am schnellsten zu finden.</li>
          <li>Der Behälter der <strong>Business Rule Exceptions</strong> bleibt außen vor — er
            lässt sich weder herauslösen noch durch einen Baustein ersetzen. Die Sprünge aus dem
            Prozess in die Beschreibung suchen ihre Ziele auf der Zeichenfläche und liefen sonst
            ins Leere.</li>
        </ul>

        <p><strong>Baustein verwenden — und die Wahl dabei:</strong></p>
        <ul>
          <li>Bei mehr als einem Baustein erscheint eine <strong>Auswahl</strong> mit Filter;
            sie zeigt je Baustein, wie oft er schon aufgerufen wird und wie viele Elemente
            er enthält. Pfeiltasten und Eingabetaste funktionieren.</li>
          <li><strong>Als Verweis</strong> — es entsteht eine <em>Call Activity</em>, die auf
            den Baustein zeigt. Wird der Baustein geändert, wirkt das an <strong>jeder</strong>
            Aufrufstelle. Das ist der Zweck der Sache.</li>
          <li><strong>Als Kopie</strong> — der Inhalt wird übernommen und ist danach
            eigenständig. Spätere Änderungen am Baustein wirken hier <strong>nicht</strong>.
            Diese Wahl trifft man bewusst, wenn eine Stelle abweichen soll.</li>
          <li>Enthält das Element, an dessen Stelle der Baustein tritt, bereits Inhalt, wird
            <strong> vorher gefragt</strong> — dieser Inhalt geht beim Einfügen verloren.</li>
        </ul>

        <p><strong>Baustein öffnen und bearbeiten:</strong></p>
        <ul>
          <li>An jeder Aufrufstelle erscheint <em>„↳ Baustein öffnen"</em>. Die Zeichenfläche
            wechselt dann auf den Baustein; oben zeigt eine Leiste, welcher es ist, und führt
            zurück zum Hauptdiagramm.</li>
          <li>Beim Ebenenwechsel geht der <strong>Rückgängig-Verlauf</strong> verloren — die
            Änderungen selbst bleiben erhalten. Vor dem Wechsel wird gefragt.</li>
        </ul>

        <p><strong>Übersicht:</strong> Im Eigenschaften-Panel listet der Abschnitt
          <em> „Bausteine"</em> alle Bausteine der Datei mit der Zahl ihrer Aufrufstellen.
          Ein Baustein ohne Aufruf ist dort gelb hervorgehoben. Entfernen lässt sich nur,
          was nirgends mehr aufgerufen wird — sonst zeigten die Aufrufe ins Leere; da sich das
          Entfernen nicht rückgängig machen lässt, wird vorher gefragt.</p>

        <div className="help-tip">
          <i className="bi bi-exclamation-triangle"></i>{' '}
          Kommt eine Datei <strong>ganz ohne gespeicherte Anordnung</strong> an, ordnet der Editor
          nur den Hauptprozess automatisch an — ein Baustein darin hat dann kein Diagramm und
          lässt sich nicht anzeigen. Er erscheint im Verzeichnis mit einem durchgestrichenen Auge
          (<i className="bi bi-eye-slash"></i>) und ohne Öffnen-Knopf an den Aufrufstellen.
          Dateien, die dieser Editor selbst geschrieben hat, sind davon nicht betroffen.
        </div>

        <p>
          Im <strong>PDF-Export</strong> bekommt jeder Baustein eine eigene Seite, überschrieben
          mit <em>„Baustein: …"</em>.
        </p>
      </>
    ),
  },

  /* ───── 6. Pools & Lanes ───── */
  {
    id: 'pools-lanes',
    icon: 'bi-layout-three-columns',
    title: 'Pools & Lanes',
    content: (
      <>
        <p>
          <strong>Pools</strong> repräsentieren Teilnehmer (z.&nbsp;B. Abteilungen, Systeme, externe Partner).
          {' '}<strong>Lanes</strong> unterteilen einen Pool in Verantwortlichkeitsbereiche.
        </p>

        <p><strong>Pool erstellen:</strong></p>
        <ol>
          <li>Ziehen Sie einen <strong>Pool / Participant</strong> aus der Palette auf die Zeichenfläche.</li>
          <li>Bestehende Elemente werden automatisch in den Pool übernommen.</li>
          <li>Benennen Sie den Pool per Doppelklick auf den seitlichen Header.</li>
        </ol>

        <p><strong>Lanes hinzufügen:</strong></p>
        <ul>
          <li>Klicken Sie auf den <strong>Pool</strong> (oder eine bestehende Lane) — das
              Kontextmenü bietet vier Wege: Lane <strong>oberhalb</strong> einfügen,
              Lane <strong>unterhalb</strong> einfügen sowie den Bereich in
              <strong> zwei</strong> oder <strong>drei Bahnen teilen</strong>.</li>
          <li>Verschieben Sie Elemente per Drag &amp; Drop zwischen Lanes, um
              Zuständigkeiten zuzuordnen.</li>
          <li>Lanes können per Doppelklick auf den Header umbenannt werden.</li>
        </ul>

        <p><strong>Mehrere Pools (Collaboration):</strong></p>
        <ul>
          <li>Erstellen Sie weitere Pools für andere Teilnehmer (z.&nbsp;B. „Kunde", „Lieferant").</li>
          <li>Verbinden Sie <strong>Elemente zwischen Pools</strong> — es werden automatisch
              {' '}<strong>Message Flows</strong> (gestrichelte Pfeile) erzeugt.</li>
          <li>Innerhalb eines Pools verwenden Sie <strong>Sequenzflüsse</strong> (durchgehende Pfeile),
              zwischen Pools ausschließlich <strong>Message Flows</strong>.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Pools erweitern sich automatisch, wenn Sie neue Elemente hinzufügen (Auto-Expand).
        </div>
      </>
    ),
  },

  /* ───── 7. Gateways & Sequenzflüsse ───── */
  {
    id: 'gateways-flows',
    icon: 'bi-signpost-split',
    title: 'Gateways & Sequenzflüsse',
    content: (
      <>
        <p>
          Gateways steuern die Verzweigung und Zusammenführung von Prozessflüssen.
          Fügen Sie sie über die <strong>Palette</strong> oder das <strong>Kontextmenü</strong> (Append) ein.
        </p>

        <p><strong>Gateway-Typen:</strong></p>
        <ul>
          <li><strong>Exclusive Gateway (XOR)</strong> – genau <em>ein</em> ausgehender Pfad wird gewählt.
              Symbol: ✕ oder leer. Beispiel: „Antrag genehmigt? → Ja / Nein"</li>
          <li><strong>Parallel Gateway (AND)</strong> – <em>alle</em> Pfade werden gleichzeitig ausgeführt.
              Symbol: +. Beispiel: „Gleichzeitig Vertrag erstellen UND Lager prüfen"</li>
          <li><strong>Inclusive Gateway (OR)</strong> – <em>einer oder mehrere</em> Pfade werden gewählt.
              Symbol: ○. Beispiel: „Per E-Mail UND/ODER per Post benachrichtigen"</li>
          <li><strong>Event-based Gateway</strong> – Entscheidung wird durch das <em>nächste eintreffende Event</em> getroffen
              (z.&nbsp;B. Timer abgelaufen vs. Nachricht empfangen)</li>
          <li><strong>Complex Gateway</strong> – benutzerdefinierte Bedingungen (selten verwendet)</li>
        </ul>

        <p><strong>Sequenzfluss-Typen:</strong></p>
        <ul>
          <li><strong>Normal:</strong> Durchgehender Pfeil — Standard-Ablauf.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-info-circle"></i>{' '}
          <strong>Bedingungen werden beschriftet, nicht konfiguriert.</strong> Dieser Editor
          dokumentiert Prozesse, er führt sie nicht aus: Ein Sequenzfluss bietet im Kontextmenü
          nur <em>Löschen</em> und <em>Farbe setzen</em>. Es gibt also weder <em>Default Flow</em>
          noch <em>Conditional Flow</em> und kein Feld für einen Bedingungsausdruck.
          Schreiben Sie die Bedingung als <strong>Beschriftung an den Pfeil</strong>
          („ja" / „nein" / „Betrag &gt; 10.000 €") — Pfeil auswählen und <kbd>E</kbd> drücken.
        </div>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          <strong>Best Practice:</strong> Jeder Split-Gateway (z.&nbsp;B. XOR-Split) sollte einen
          dazugehörigen Join-Gateway haben, um Pfade sauber zusammenzuführen.
        </div>
      </>
    ),
  },

  /* ───── Business Rule Exceptions ───── */
  {
    id: 'business-rule-exceptions',
    icon: 'bi-exclamation-octagon',
    title: 'Business Rule Exceptions (BRE)',
    content: (
      <>
        <p>
          Eine <strong>Business Rule Exception</strong> beschreibt einen fachlichen Abbruchgrund
          einmal an zentraler Stelle und verweist von jeder Stelle im Prozess darauf, an der er
          auftreten kann. Beschrieben werden sie in einem eingeklappten Sub-Prozess
          <strong> „BusinessRuleExceptions"</strong>; im Prozess trägt ein Ereignis den Verweis.
        </p>

        <p><strong>Ausnahme anlegen — zwei Wege:</strong></p>
        <ol>
          <li>
            <strong>Am Ereignis:</strong> Ende-Ereignis anklicken und im Kontext-Pad
            {' '}<strong>„Als BRE auslösen"</strong> wählen. Gibt es noch keine Ausnahme, entsteht mit
            einem Klick der Behälter, die erste Ausnahme <em>BRE1</em> und die Verknüpfung.
            Sind schon welche beschrieben, erscheint eine Auswahl.
          </li>
          <li>
            <strong>Im Eigenschaften-Panel:</strong> Gruppe <strong>„Business Rule Exceptions"</strong>
            {' '}öffnen und <strong>„+ Neue BRE"</strong> klicken. Dort lassen sich Bezeichnung und
            Beschreibung eintragen und Ausnahmen wieder entfernen (<strong>✕</strong>).
          </li>
        </ol>

        <ul>
          <li>Die Codes (<em>BRE1</em>, <em>BRE2</em> …) werden automatisch vergeben; ein
              gelöschter Code wird wieder frei.</li>
          <li>Ist ein Ereignis ausgewählt, zeigt die Gruppe zusätzlich <strong>„Löst aus"</strong> —
              damit lässt sich der Verweis wechseln oder lösen.</li>
          <li>Wird eine Ausnahme gelöscht, verlieren die zugehörigen Ereignisse ihren Verweis,
              bleiben aber als Ereignis erhalten.</li>
          <li>Ein <strong>Klick auf ein verweisendes Ereignis</strong> springt zur Beschreibung;
              dort führt <strong>„↩ Zurück zum Prozess"</strong> genau an die Ausgangsstelle zurück.</li>
        </ul>

        <p>
          Selbst modellierte Ausnahmen sind von importierten nicht zu unterscheiden: Signavio-Archive
          bringen ihre BREs im selben Format mit, Navigation und Liste behandeln beide gleich.
        </p>
      </>
    ),
  },

  /* ───── SharePoint ───── */
  {
    id: 'sharepoint',
    icon: 'bi-cloud',
    title: 'SharePoint: laden & speichern',
    content: (
      <>
        <p>
          Prozesse liegen gemeinsam in SharePoint unter <code>Kundenprojekte/</code>.
          Sowohl <strong>Laden</strong> als auch <strong>Speichern</strong> fragen zuerst nach
          der Quelle bzw. dem Ziel — <strong>SharePoint</strong> oder <strong>lokale Datei</strong>.
        </p>

        <p><strong>Im Ladedialog:</strong></p>
        <ul>
          <li><strong>Zuletzt verwendet</strong> steht oben: Ihre zuletzt geöffneten Dateien,
              zunächst die fünf jüngsten. <em>Alle anzeigen</em> klappt die vollständige Liste auf.</li>
          <li>Der <strong>Pfad</strong> darunter ist anklickbar — jeder Bestandteil springt in
              den jeweiligen Ordner; der <strong>Pfeil nach oben</strong> geht eine Ebene zurück.</li>
          <li>Gezeigt werden Ordner sowie <code>.bpmn</code>-, <code>.xml</code>- und
              <code>.sgx</code>-Dateien; Archive tragen ein 🗜-Symbol.</li>
        </ul>

        <p><strong>Beim Speichern</strong> wählen Sie den Ordner auf demselben Weg und tragen unten
        den Dateinamen ein. Archive erscheinen dabei nicht — geschrieben wird immer BPMN.</p>

        <div className="help-tip">
          <i className="bi bi-exclamation-triangle"></i>{' '}
          <strong>Gleichzeitiges Bearbeiten:</strong> Hat jemand die Datei in SharePoint geändert,
          seit Sie sie geladen haben, bricht das Speichern mit einem Hinweis ab, statt die fremde
          Änderung zu überschreiben. Laden Sie die Datei dann neu und speichern Sie erneut —
          Ihre Zwischenstände gehen dabei verloren, sichern Sie sie vorher über
          <strong> Speichern unter…</strong> lokal.
        </div>

        <p>
          Schlägt das Speichern in SharePoint aus einem anderen Grund fehl, weicht der Editor
          automatisch auf den lokalen Weg aus, damit Ihre Arbeit nicht verloren geht.
          Fehlt die Berechtigung oder die Anmeldung, meldet er das ausdrücklich.
        </p>
      </>
    ),
  },

  /* ───── Sitzung ───── */
  {
    id: 'session',
    icon: 'bi-shield-lock',
    title: 'Anmeldung & Sitzungsende',
    content: (
      <>
        <p>
          Der Editor meldet Sie über Ihr Microsoft-Konto an; rechts oben stehen Ihr Name und
          der Knopf <strong>Abmelden</strong> (Logout).
        </p>

        <div className="help-tip">
          <i className="bi bi-exclamation-triangle"></i>{' '}
          <strong>Automatische Abmeldung: Nach 30 Minuten ohne Eingabe werden Sie
          abgemeldet</strong> — ein Zeitlimit (Timeout), das sich nicht abschalten lässt.
          Der Editor lädt dann die Anmeldeseite — <strong>ungespeicherte Änderungen sind verloren</strong>.
          Als Aktivität zählen Mausbewegung, Tastendruck, Scrollen und Klicks. Wer eine längere
          Besprechung vor sich hat, speichert vorher.
        </div>

        <p>
          Beim Laden oder Speichern kann die Anmeldung ablaufen; dann öffnet Microsoft ein
          Anmeldefenster. Erscheint stattdessen die Meldung, die Anmeldung werde neu gestartet,
          wiederholen Sie den Vorgang nach dem Anmelden.
        </p>
      </>
    ),
  },

  /* ───── 8. Boundary Events & Event-Subprozesse ───── */
  {
    id: 'boundary-events',
    icon: 'bi-lightning',
    title: 'Boundary Events & Event-Subprozesse',
    content: (
      <>
        <p>
          <strong>Boundary Events</strong> werden am Rand eines Tasks oder Sub-Prozesses verankert
          und reagieren auf Ereignisse, die <em>während</em> der Ausführung eintreten.
        </p>

        <p><strong>Boundary Event erstellen:</strong></p>
        <ol>
          <li>Ziehen Sie ein <strong>Intermediate Event</strong> aus der Palette
              direkt auf den <strong>Rand</strong> eines Tasks oder Sub-Prozesses.</li>
          <li>Das Event „rastet" am Rand ein und wird als Boundary Event verankert.</li>
          <li>Wählen Sie über den <strong>Schraubenschlüssel</strong> (Replace-Menü)
              den gewünschten Typ.</li>
        </ol>

        <p><strong>Verfügbare Boundary-Event-Typen:</strong></p>
        <ul>
          <li><strong>Timer</strong> – nach Ablauf einer Zeitdauer oder zu einem festen Zeitpunkt (Uhr-Symbol)</li>
          <li><strong>Error</strong> – fängt Fehler aus Sub-Prozessen ab (Blitz-Symbol)</li>
          <li><strong>Message</strong> – reagiert auf eingehende Nachrichten (Brief-Symbol)</li>
          <li><strong>Signal</strong> – reagiert auf Broadcast-Signale</li>
          <li><strong>Escalation</strong> – Eskalation an übergeordnete Ebene</li>
          <li><strong>Compensation</strong> – löst Kompensations-Handler aus</li>
          <li><strong>Conditional</strong> – reagiert auf geänderte Datenbedingungen</li>
        </ul>

        <p><strong>Interrupting vs. Non-Interrupting:</strong></p>
        <ul>
          <li><strong>Interrupting</strong> (durchgehender Rand): Unterbricht die laufende Aktivität
              und leitet den Fluss um. Beispiel: Timer-Timeout bricht Task ab.</li>
          <li><strong>Non-Interrupting</strong> (gestrichelter Rand): Die Aktivität läuft weiter,
              ein <em>zusätzlicher</em> Pfad wird gestartet. Beispiel: Erinnerung alle 24 Stunden.</li>
          <li>Wechseln Sie zwischen beiden Varianten über den Schraubenschlüssel.</li>
        </ul>

        <p><strong>Event-Subprozesse:</strong></p>
        <ul>
          <li>Wandeln Sie einen Sub-Prozess über den <strong>Schraubenschlüssel</strong> in einen
              {' '}<strong>Event Sub-Process</strong> um (gestrichelter Rand).</li>
          <li>Setzen Sie den <strong>Start-Event-Typ</strong> (z.&nbsp;B. Error-Start, Message-Start, Timer-Start).</li>
          <li>Event-Subprozesse eignen sich für globale Ausnahmen oder Ereignisse,
              z.&nbsp;B. „Systemausfall" oder „Stornierung durch Kunden".</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Error Boundary Events an Sub-Prozessen sind die gängigste Methode zur Fehlerbehandlung in BPMN.
        </div>
      </>
    ),
  },

  /* ───── 9. Datenobjekte & Annotationen ───── */
  {
    id: 'data-annotations',
    icon: 'bi-file-earmark-text',
    title: 'Datenobjekte & Annotationen',
    content: (
      <>
        <p><strong>Datenobjekte &amp; Datenspeicher:</strong></p>
        <ul>
          <li>Ziehen Sie <strong>Datenobjekte</strong> (Blatt-Symbol) oder
              {' '}<strong>Datenspeicher</strong> (Zylinder-Symbol) aus der Palette auf die Zeichenfläche.</li>
          <li>Verbinden Sie sie über das Kontextmenü mit Tasks —
              es werden <strong>Data Associations</strong> (gestrichelte Pfeile) erzeugt.</li>
          <li>Beispiele: „Kundendaten", „Angebotsdokument", „Datenbank".</li>
          <li>Datenobjekte zeigen an, welche Informationen in einem Schritt gelesen oder erzeugt werden.</li>
        </ul>

        <p><strong>Text-Annotationen:</strong></p>
        <ul>
          <li>Wählen Sie <strong>Textannotation</strong> aus der Palette oder dem Kontextmenü.</li>
          <li>Verbinden Sie die Annotation mit dem zugehörigen Element (Assoziationslinie).</li>
          <li>Nutzen Sie Annotationen, um komplexe fachliche Regeln oder Sonderfälle
              kurz textlich zu erläutern, ohne den Ablauf zu überfrachten.</li>
        </ul>

        <p><strong>Message Flows:</strong></p>
        <ul>
          <li>Message Flows (gestrichelte Pfeile mit Kreis/Pfeilspitze) verbinden
              Elemente <strong>zwischen verschiedenen Pools</strong>.</li>
          <li>Sie modellieren externe Kommunikation (z.&nbsp;B. „Bestellung senden" an Lieferant-Pool).</li>
          <li>Innerhalb eines Pools werden ausschließlich <strong>Sequenzflüsse</strong> verwendet.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Datenobjekte beeinflussen nicht den Kontrollfluss — sie dokumentieren den Informationsfluss.
        </div>
      </>
    ),
  },

  /* ───── 10. Erweiterte Funktionen ───── */
  {
    id: 'advanced-features',
    icon: 'bi-gear',
    title: 'Erweiterte Funktionen',
    content: (
      <>
        <p><strong>Call Activity:</strong></p>
        <ul>
          <li>Wandeln Sie einen Task über den <strong>Schraubenschlüssel</strong> in eine
              {' '}<strong>Call Activity</strong> um (wird mit fettem Rand dargestellt).</li>
          <li>Call Activities verweisen auf einen <strong>externen Prozess</strong>, der
              separat modelliert wird — ideal für wiederverwendbare Teilprozesse.</li>
          <li>Beispiel: „Kreditprüfung durchführen" als Call Activity, die in
              mehreren Hauptprozessen referenziert wird.</li>
        </ul>

        <p><strong>Multi-Instance Marker:</strong></p>
        <ul>
          <li>Markieren Sie einen Task oder Sub-Prozess über den <strong>Schraubenschlüssel</strong>:
              ganz oben im Menü sitzen dafür eigene Symbole —
              <em>Parallel multi-instance</em>, <em>Sequential multi-instance</em> und <em>Loop</em>,
              bei Sub-Prozessen zusätzlich <em>Ad-hoc</em>. Ein zweiter Klick nimmt den Marker
              wieder weg.</li>
          <li><strong>Parallel (|||)</strong> – alle Instanzen werden gleichzeitig ausgeführt.
              Beispiel: „Prüfung für jeden Standort gleichzeitig".</li>
          <li><strong>Sequenziell (≡)</strong> – Instanzen werden nacheinander ausgeführt.
              Beispiel: „Jeden Posten einzeln verbuchen".</li>
          <li>Die drei kleinen Striche am unteren Rand des Elements zeigen den Marker an.</li>
        </ul>

        <p><strong>Transaction Subprocess:</strong></p>
        <ul>
          <li>Wandeln Sie einen <strong>aufgeklappten</strong> Sub-Prozess über den
              Schraubenschlüssel in eine <strong>Transaction</strong> um (doppelter Rand).
              Ein eingeklappter Sub-Prozess bietet den Eintrag nicht an — vorher aufklappen.</li>
          <li>Transaktionen sind atomar: Entweder werden alle Schritte abgeschlossen,
              oder bei Fehler wird ein Rollback (Kompensation) ausgelöst.</li>
          <li>Nutzen Sie <strong>Compensation Boundary Events</strong> und
              {' '}<strong>Compensation Handler</strong> für die Rückabwicklung.</li>
        </ul>

        <p><strong>Loop Marker:</strong></p>
        <ul>
          <li>Ebenfalls über die Symbolzeile des Schraubenschlüssel-Menüs: <em>Loop</em> (↻-Symbol).</li>
          <li>Der Task wird so lange wiederholt, bis eine Abbruchbedingung erfüllt ist.
              Die Bedingung selbst hält dieser Editor nicht fest — schreiben Sie sie in den
              Namen des Elements oder in die <strong>Documentation</strong>.</li>
        </ul>
      </>
    ),
  },

  /* ───── 11. Eigenschaften-Panel ───── */
  {
    id: 'properties',
    icon: 'bi-sliders',
    title: 'Eigenschaften-Panel',
    content: (
      <>
        <p>
          Am rechten Rand öffnet sich automatisch das <strong>Eigenschaften-Panel</strong>,
          sobald ein Element ausgewählt ist. Das Panel ist in der Breite veränderbar (Ziehen am linken Rand).
        </p>

        <p>
          <strong>Den Namen eines Elements ändern Sie nicht im Panel</strong>, sondern direkt
          auf der Zeichenfläche: doppelklicken oder das Element auswählen und <kbd>E</kbd> drücken.
          Das Panel zeigt bewusst nur die fachlichen Angaben.
        </p>

        <p>Welche Gruppen erscheinen, hängt vom ausgewählten Element ab:</p>
        <ul>
          <li><strong>Documentation</strong> – mehrzeilige Beschreibung für fachliche Details.
              Bei <em>jedem</em> Element vorhanden.</li>
          <li><strong>Bild</strong> – Screenshot zum Element, siehe <em>„Screenshots hinzufügen"</em>.</li>
          <li><strong>Video</strong> und <strong>Video-Timestamp (Sekunden)</strong> – verknüpft eine
              Stelle im zugeordneten Video.</li>
          <li><strong>Externe Links</strong> – verknüpft Webseiten mit einem Element (s.&nbsp;u.).</li>
          <li><strong>Sub-Prozesse</strong> – der Baum aller Sub-Prozesse mit
              <em> Alle ausklappen</em> / <em>Alle einklappen</em>, sobald das Diagramm welche enthält.</li>
          <li><strong>Business Rule Exceptions</strong> – siehe eigenen Abschnitt.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-info-circle"></i>{' '}
          Klicken Sie auf eine leere Stelle der Zeichenfläche, um die Gruppen zu sehen, die zum
          ganzen Diagramm gehören statt zu einem einzelnen Element.
        </div>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Elemente mit Dokumentation erhalten ein 📝-Badge, solche mit Bildern ein 🖼-Badge
          und solche mit Video-Zeitstempel ein 🎬-Badge als Overlay im Diagramm.
        </div>
      </>
    ),
  },

  /* ───── 11b. Links & Tooltips ───── */
  {
    id: 'links-tooltips',
    icon: 'bi-link-45deg',
    title: 'Externe Links & Tooltips',
    content: (
      <>
        <p><strong>Externe Links:</strong></p>
        <ul>
          <li>Im Bereich <strong>Externe Links</strong> können Sie beliebig viele URLs
              an Tasks, Sub-Prozesse oder den Hauptprozess anhängen.</li>
          <li>Geben Sie eine <strong>URL</strong> (http:// oder https://) und optional
              einen <strong>Titel</strong> ein und klicken Sie auf <strong>Hinzufügen</strong>.</li>
          <li>Klick auf einen Link öffnet die Seite in einem <strong>Vorschau-Modal</strong>{' '}
              (eingebettete Ansicht innerhalb des Editors).</li>
          <li>Im Modal können Sie die Seite über den Button <strong>Neuer Tab</strong> in einem
              neuen Browsertab öffnen.</li>
          <li>Links lassen sich jederzeit über das <strong>Papierkorb-Symbol</strong> entfernen.</li>
          <li>Die Links werden zusammen mit dem Diagramm in der <code>.bpmn</code>-Datei gespeichert.</li>
        </ul>

        <p><strong>Hover-Tooltips (View-Modus):</strong></p>
        <ul>
          <li>Im <strong>View-Modus</strong> zeigt das Überfahren eines Elements mit der Maus
              einen <strong>Tooltip</strong> mit folgenden Informationen:
            <ul>
              <li>Element-Name</li>
              <li>Angehängtes Bild (klickbar zur Lightbox)</li>
              <li>Dokumentationstext</li>
              <li>Video-Zeitstempel (klickbar zum Abspielen)</li>
            </ul>
          </li>
        </ul>

        <p>
          Bei der Auswahl des <strong>gesamten Prozesses</strong> (Klick auf den Hintergrund)
          erscheinen die Prozess-Eigenschaften inkl. Video-URL.
        </p>
      </>
    ),
  },

  /* ───── 12. Screenshots hinzufügen ───── */
  {
    id: 'screenshots',
    icon: 'bi-image',
    title: 'Screenshots hinzufügen',
    content: (
      <>
        <p>
          Sie können <strong>Bilder und Screenshots</strong> an einzelne Elemente (Tasks, Events etc.)
          anhängen. Diese werden als <strong>Overlay</strong> direkt im Diagramm angezeigt.
        </p>

        <p><strong>Bild manuell hochladen:</strong></p>
        <ul>
          <li>
            Wählen Sie ein Element im Diagramm aus und öffnen Sie im
            {' '}<strong>Eigenschaften-Panel</strong> die Gruppe <strong>Bild</strong>.
          </li>
          <li>
            Klicken Sie auf <strong>Bild hinzufügen</strong> und wählen Sie eine Bilddatei
            von Ihrem Computer (PNG oder JPG, max. 10 MB).
          </li>
        </ul>

        <p><strong>Automatische Bildoptimierung:</strong></p>
        <ul>
          <li>
            Hochgeladene Bilder werden <strong>automatisch verkleinert</strong> (max. 1920 × 1080 Pixel)
            und als <strong>komprimiertes JPEG</strong> gespeichert, um die Dateigröße gering zu halten.
          </li>
          <li>
            Bilder mit <strong>Transparenz</strong> (PNG mit Alpha-Kanal) behalten
            ihr PNG-Format bei.
          </li>
          <li>
            Auch beim <strong>Zuschneiden</strong> in der Lightbox wird das Ergebnis
            automatisch optimiert.
          </li>
        </ul>

        <p><strong>Screenshot aus dem Video-Player:</strong></p>
        <ul>
          <li>
            Wählen Sie einen Task im Diagramm aus, navigieren Sie im Video zur
            gewünschten Stelle und klicken Sie auf <strong>📸 Screenshot zuweisen</strong>.
          </li>
          <li>
            Das aktuelle Videobild wird als Bild-Overlay an den Task angehängt.
            Gleichzeitig wird der <strong>Zeitstempel</strong> mit zugewiesen.
          </li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Elemente mit Bildern erhalten ein <strong>🖼-Badge</strong> als Overlay im Diagramm.
          Bilder werden direkt in der BPMN-Datei gespeichert und
          bleiben auch ohne Zugang zum Originalvideo erhalten.
        </div>
      </>
    ),
  },

  /* ───── 12b. Lightbox & Bildbearbeitung ───── */
  {
    id: 'lightbox',
    icon: 'bi-crop',
    title: 'Lightbox & Bildbearbeitung',
    content: (
      <>
        <p><strong>Lightbox &amp; Zuschneiden:</strong></p>
        <ul>
          <li>
            Klicken Sie auf ein <strong>Bild-Overlay</strong> im Diagramm, um es in der
            {' '}<strong>Lightbox</strong> vergrößert anzuzeigen.
          </li>
          <li>
            Im <strong>Edit-Modus</strong> können Sie das Bild in der Lightbox{' '}
            <strong>zuschneiden</strong> (Crop):
            Klicken Sie auf <strong>„Zuschneiden"</strong>, ziehen Sie den Ausschnitt auf
            und bestätigen Sie mit <strong>„Übernehmen"</strong>.
            Das zugeschnittene Bild ersetzt das Original am Element.
          </li>
        </ul>

        <p><strong>CORS-Anforderungen (Video-Screenshots):</strong></p>
        <ul>
          <li>
            Für die Screenshot-Funktion bei URL-Videos muss der Video-Server
            {' '}<strong>CORS-Header</strong> senden (<code>Access-Control-Allow-Origin</code>).
            Ohne CORS wird das Video trotzdem abgespielt, aber Screenshots sind nicht verfügbar.
          </li>
          <li>
            <strong>Lokale Dateien</strong> (über 📁 Datei) haben keine CORS-Einschränkung —
            Screenshots funktionieren immer.
          </li>
          <li>
            Falls Sie Videos von einem eigenen Server laden (z.&nbsp;B. nginx), fügen Sie
            in der Server-Konfiguration hinzu:<br />
            <code>add_header Access-Control-Allow-Origin &quot;*&quot; always;</code>
          </li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Durch die automatische Komprimierung bleibt die Dateigröße auch bei
          vielen Screenshots handhabbar.
        </div>
      </>
    ),
  },

  /* ───── 13. Video-Player ───── */
  {
    id: 'video',
    icon: 'bi-camera-video',
    title: 'Video-Player',
    content: (
      <>
        <p>
          Über den <strong>Video</strong>-Button in der Werkzeugleiste öffnen Sie den
          integrierten, verschiebbaren Video-Player.
        </p>

        <p><strong>Video laden — zwei Möglichkeiten:</strong></p>
        <ul>
          <li>
            <strong>URL eingeben:</strong> Geben Sie eine <strong>direkte Video-URL</strong> ein
            (MP4, WebM etc.) und klicken Sie auf <strong>Laden</strong>.
            Die URL wird in der BPMN-Datei gespeichert und beim nächsten Öffnen automatisch geladen.
          </li>
          <li>
            <strong>Lokale Datei hochladen:</strong> Klicken Sie auf <strong>📁 Datei</strong> und
            wählen Sie eine Videodatei von Ihrem Computer.
            {' '}<em>Hinweis:</em> Lokale Videos werden <strong>nicht</strong> in der BPMN-Datei gespeichert —
            beim nächsten Laden muss die Datei erneut ausgewählt werden.
            Screenshots, die Sie aus dem Video einem Task zuweisen, bleiben hingegen
            als Base64-Bilder dauerhaft in der BPMN-Datei erhalten.
          </li>
        </ul>

        <p><strong>Unterstützte Formate:</strong></p>
        <ul>
          <li>Direkte Videodateien: <strong>MP4</strong>, <strong>WebM</strong>, <strong>OGG</strong></li>
          <li><strong>YouTube-URLs werden nicht unterstützt</strong> — der Player verwendet ein
              HTML5-Video-Element, das nur direkte Videodateien abspielen kann
              (keine Webseiten-URLs wie <code>youtube.com/watch?v=…</code>).</li>
        </ul>

        <p><strong>Zeitstempel &amp; Navigation:</strong></p>
        <ul>
          <li>
            <strong>Zeitstempel zuweisen:</strong> Wählen Sie einen Task im Diagramm aus,
            navigieren Sie im Video zur gewünschten Stelle und klicken Sie auf
            {' '}<strong>⏱ Timestamp zuweisen</strong>.
          </li>
          <li>
            <strong>Navigieren:</strong> Klicken Sie auf das 🎬-Overlay eines Elements im Diagramm,
            um direkt zur zugehörigen Stelle im Video zu springen.
          </li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Der Video-Button wird grün, sobald eine Video-URL hinterlegt ist.
          Zum Hinzufügen von Screenshots aus dem Video siehe Abschnitt <em>„Screenshots hinzufügen"</em>.
        </div>
      </>
    ),
  },

  /* ───── 14. Versionsverwaltung ───── */
  {
    id: 'versions',
    icon: 'bi-tag',
    title: 'Versionsverwaltung',
    content: (
      <>
        <p>
          Klicken Sie auf <strong>Version</strong> in der Werkzeugleiste, um das
          Versionsdialogfenster zu öffnen.
        </p>

        <p><strong>Versionsnummer &amp; Autor:</strong></p>
        <ul>
          <li>Die aktuelle <strong>Versionsnummer</strong> und der <strong>Bearbeiter</strong> werden
              oben im Dialog angezeigt.</li>
          <li>Die aktuelle Versionsnummer erscheint auch als Badge in der Werkzeugleiste.</li>
        </ul>

        <p><strong>Neue Version erstellen:</strong></p>
        <ul>
          <li><strong>Sub-Version</strong> (z.&nbsp;B. 1.0 → 1.1) – für kleinere Änderungen.</li>
          <li><strong>Haupt-Version</strong> (z.&nbsp;B. 1.0 → 2.0) – für größere Überarbeitungen.</li>
          <li>Vor dem Erstellen können Sie einen <strong>Kommentar</strong> (max. 500 Zeichen)
              eingeben, um die Änderungen zu beschreiben.</li>
          <li>Beim Erstellen einer neuen Version wird der <strong>saubere Zustand</strong> des
              Diagramms (vor Ihren Änderungen) als XML-Snapshot der alten Version gespeichert.
              So kann später exakt auf diesen Stand zurückgerollt werden.</li>
          <li>Enthaltene <strong>Bilder</strong> werden dabei automatisch <strong>dedupliziert</strong>:
              Unveränderte Bilder werden nur einmal gespeichert und über SHA-256-Hashes mit den
              einzelnen Versionen verknüpft.</li>
          <li>Die Snapshots selbst werden <strong>komprimiert</strong> abgelegt. Zusammen mit der
              Begrenzung auf die 10 jüngsten Stände bleibt die Dateigröße auch nach vielen
              Speichervorgängen beschränkt, statt mit jeder Version weiterzuwachsen.</li>
        </ul>

        <p><strong>Automatische Snapshots beim Speichern:</strong></p>
        <ul>
          <li>Auch beim normalen <strong>Speichern</strong> (ohne explizite Versionserstellung) wird
              automatisch ein XML-Snapshot des aktuellen Stands in die Versionshistorie geschrieben.</li>
          <li>Diese Einträge ermöglichen ebenfalls einen Rollback auf den jeweiligen Speicherstand.</li>
          <li><strong>Zweimal speichern ohne Änderung erzeugt nur einen Eintrag.</strong> Ein
              Speichern, das nichts verändert hat, ist keine Version — die Liste bleibt dann
              unverändert.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Alle Versionsdaten (Snapshots, Bilder, Historie) werden direkt in der <code>.bpmn</code>-Datei
          gespeichert — es wird kein externer Server benötigt.
        </div>
      </>
    ),
  },

  /* ───── 14b. Versionshistorie ───── */
  {
    id: 'version-history',
    icon: 'bi-clock-history',
    title: 'Versionshistorie',
    content: (
      <>
        <p><strong>Versionshistorie im Dialog (Edit-Modus):</strong></p>
        <ul>
          <li>Unterhalb der Versionssteuerung wird die <strong>Bearbeitungshistorie</strong>{' '}
              als Tabelle angezeigt (Version, Autor, Kommentar, Datum).</li>
          <li>Jeder Eintrag mit gespeichertem XML-Snapshot zeigt einen{' '}
              <strong>Rollback-Button</strong> (<i className="bi bi-arrow-counterclockwise"></i>).
              Klicken Sie darauf, um auf diese Version zurückzurollen (siehe Abschnitt „Rollback").</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-exclamation-triangle"></i>{' '}
          <strong>Zurückrollen können Sie auf die 10 jüngsten Stände.</strong> Ältere Einträge
          bleiben als Protokollzeile bestehen — mit Version, Autor, Datum und Kommentar —,
          verlieren aber ihren Snapshot und damit den Rollback-Knopf. Die Liste selbst wird
          <strong>beim Öffnen der Datei</strong> auf die 50 jüngsten Einträge gekürzt. Wer einen
          bestimmten Stand dauerhaft aufheben will, legt ihn über
          <strong> Speichern unter…</strong> als eigene Datei ab.
        </div>

        <div className="help-tip">
          <i className="bi bi-info-circle"></i>{' '}
          <strong>Nach einem Import</strong> (Signavio-Archiv oder bereinigter Signavio-Export)
          trägt der <em>erste</em> gespeicherte Eintrag bewusst <strong>keinen Snapshot</strong> und
          damit auch keinen Rollback-Knopf: Es gäbe keinen früheren Stand, auf den er zurückführen
          könnte, und die Datei würde sich durch die eingebettete Selbstkopie verdoppeln.
          Ab der zweiten Version läuft alles wie gewohnt.
        </div>

        <p><strong>Versionshistorie im Dialog (View-Modus):</strong></p>
        <ul>
          <li>Im View-Modus zeigen die Einträge stattdessen ein <strong>Augen-Symbol</strong>{' '}
              (<i className="bi bi-eye"></i>), um die Version schreibgeschützt zu betrachten.</li>
          <li>Das Diagramm wechselt automatisch in den View-Modus.</li>
          <li>Die aktuell angezeigte Version wird in der Historien-Tabelle{' '}
              <strong>blau hervorgehoben</strong>.</li>
          <li>Über die Zeile <strong>„Aktueller Stand"</strong> am oberen Rand der Tabelle
              kehren Sie jederzeit zum aktuellen Arbeitsstand zurück.</li>
          <li>Wenn Sie sich in einem <strong>Sub-Prozess</strong> befinden, versucht der Editor,
              dieselbe Ebene in der historischen Version anzuzeigen. Falls der Sub-Prozess
              in der älteren Version noch nicht existiert, erscheint ein Hinweis-Dialog.</li>
          <li>Beim Wechsel zurück in den <strong>Edit-Modus</strong> wird immer der
              aktuelle Arbeitsstand wiederhergestellt — es besteht keine Gefahr,
              versehentlich eine alte Version zu bearbeiten.</li>
        </ul>

        <p><strong>Historien-Panel (View-Modus):</strong></p>
        <ul>
          <li>Im View-Modus erscheint am unteren Rand ein <strong>Historien-Panel</strong> mit
              der vollständigen Versionshistorie.</li>
          <li>Das Panel kann über den <strong>Anfasser am oberen Rand</strong> in der Höhe
              stufenlos angepasst werden (Drag nach oben/unten).</li>
          <li>Innerhalb des Panels scrollt die Tabelle automatisch, wenn viele Einträge vorhanden sind.</li>
        </ul>
      </>
    ),
  },

  /* ───── 14c. Rollback ───── */
  {
    id: 'rollback',
    icon: 'bi-arrow-counterclockwise',
    title: 'Version-Rollback',
    content: (
      <>
        <p>
          Mit dem <strong>Rollback</strong> können Sie das Diagramm auf einen früheren
          Versionsstand zurücksetzen. Alle neueren Versionen werden dabei{' '}
          <strong>unwiderruflich gelöscht</strong>.
        </p>

        <p><strong>So führen Sie einen Rollback durch:</strong></p>
        <ol>
          <li>Öffnen Sie die <strong>Versionsverwaltung</strong> über den Version-Button in der Werkzeugleiste.</li>
          <li>In der Bearbeitungshistorie finden Sie neben jedem Eintrag einen roten{' '}
              <strong>Zurückrollen-Button</strong> (<i className="bi bi-arrow-counterclockwise"></i>).</li>
          <li>Klicken Sie auf den Button der gewünschten Zielversion.</li>
          <li>Es erscheint ein <strong>Bestätigungsdialog</strong> mit folgenden Informationen:
            <ul>
              <li>Die <strong>Zielversion</strong>, auf die zurückgerollt wird.</li>
              <li>Eine Liste aller <strong>Versionen, die gelöscht</strong> werden.</li>
              <li>Ein Hinweis, dass die Aktion <strong>nicht rückgängig</strong> gemacht werden kann.</li>
            </ul>
          </li>
          <li><strong>Optional</strong>: Klicken Sie auf <strong>Backup herunterladen</strong>,
              um den aktuellen Stand als <code>.bpmn</code>-Datei zu sichern, bevor Sie fortfahren.</li>
          <li>Aktivieren Sie die <strong>Bestätigungs-Checkbox</strong> und klicken Sie auf{' '}
              <strong>Endgültig zurückrollen</strong>.</li>
        </ol>

        <p><strong>Was passiert beim Rollback:</strong></p>
        <ul>
          <li>Das Diagramm wird auf den <strong>exakten Stand</strong> der gewählten Version
              zurückgesetzt — inklusive aller Elemente, Positionen, Farben und Bilder.</li>
          <li>Die Versionshistorie wird <strong>gekürzt</strong>: Alle Einträge nach der
              Zielversion werden entfernt.</li>
          <li>Der <strong>Image-Store</strong> wird automatisch aufgeräumt (nicht mehr
              referenzierte Bilder werden entfernt).</li>
          <li>Die <strong>Versionsnummer</strong> wird auf die Zielversion zurückgesetzt.</li>
          <li>Das Diagramm hat danach <strong>ungespeicherte Änderungen</strong> — speichern
              Sie es, um den Rollback dauerhaft zu übernehmen.</li>
        </ul>

        <div className="help-warning">
          <i className="bi bi-exclamation-triangle"></i>{' '}
          <strong>Achtung:</strong> Ein Rollback ist endgültig. Laden Sie bei Bedarf vorher
          ein Backup herunter, um den aktuellen Stand zu sichern.
        </div>
      </>
    ),
  },

  /* ───── 15. Ansichts- & Bearbeitungsmodus ───── */
  {
    id: 'modes',
    icon: 'bi-eye',
    title: 'Ansichts- & Bearbeitungsmodus',
    content: (
      <>
        <p>
          Über den <strong>Edit / View</strong>-Button wechseln Sie zwischen den Modi:
        </p>
        <ul>
          <li>
            <strong>Edit-Modus:</strong> Volles Bearbeiten – Elemente erstellen, verschieben,
            verbinden, Eigenschaften ändern, speichern.
          </li>
          <li>
            <strong>View-Modus:</strong> Schreibgeschützt – Diagramm betrachten, zoomen
            und als PDF exportieren. Keine Änderungen möglich.
            <ul>
              <li><strong>Hover-Tooltips:</strong> Beim Überfahren eines Elements mit der Maus
                  erscheint ein Tooltip mit Name, Dokumentation, Bild und Video-Zeitstempel.</li>
              <li><strong>Historien-Panel:</strong> Am unteren Rand erscheint die Versionshistorie
                  mit der Möglichkeit, ältere Versionen direkt anzuzeigen (siehe Abschnitt „Versionsverwaltung").</li>
              <li>Das Historien-Panel ist in der <strong>Höhe veränderbar</strong> –
                  ziehen Sie den Anfasser am oberen Rand nach oben oder unten.</li>
              <li><strong>Bausteine</strong> lassen sich auch hier öffnen und betrachten;
                  angelegt und eingefügt werden sie nur im Edit-Modus.</li>
            </ul>
          </li>
        </ul>
        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Der aktuelle Modus wird als farbiges Badge angezeigt: <strong>EDIT</strong> (gelb) oder <strong>VIEW</strong> (blau).
          Beim Wechsel von View zurück zu Edit wird immer der aktuelle Arbeitsstand geladen —
          auch wenn Sie zuvor eine ältere Version betrachtet haben.
        </div>
      </>
    ),
  },

  /* ───── 16. PDF-Export ───── */
  {
    id: 'pdf-export',
    icon: 'bi-file-earmark-pdf',
    title: 'PDF-Export',
    content: (
      <>
        <p>
          Nur im <strong>Ansichtsmodus</strong> erscheint in der Werkzeugleiste ein weiterer Knopf
          mit PDF-Symbol (<i className="bi bi-file-earmark-pdf"></i>, Tooltip
          <em> „Als PDF exportieren"</em>). Er erstellt ein mehrseitiges PDF-Dokument:
        </p>
        <ul>
          <li><strong>Seite 1:</strong> Das Hauptdiagramm als Vektorgrafik — beliebig
            skalierbar ohne Qualitätsverlust, Beschriftungen bleiben als Text durchsuchbar.</li>
          <li><strong>Weitere Seiten:</strong> Jeder eingeklappte Sub-Prozess wird automatisch
            als eigenes BPMN-Diagramm auf einer separaten Seite gerendert.</li>
          <li><strong>Bausteine:</strong> Auch jeder wiederverwendbare Baustein bekommt eine eigene
            Seite, überschrieben mit <em>„Baustein: …"</em> (siehe Abschnitt „Bausteine").</li>
          <li><strong>Task-Dokumentation:</strong> Am Ende des PDFs werden alle Tasks und Sub-Prozesse
            aufgelistet, die <strong>Dokumentationstext</strong> oder <strong>Bilder</strong> enthalten.
            Pro Element werden Name, Dokumentation und eingebettete Bilder dargestellt. Bilder
            werden dabei als JPEG mit 70 % Qualität eingebettet, um die Dateigröße zu begrenzen.</li>
        </ul>
        <p>
          Das PDF wird automatisch heruntergeladen und trägt den Prozessnamen als Dateinamen.
        </p>
      </>
    ),
  },

  /* ───── 17. Token-Simulation ───── */
  {
    id: 'token-simulation',
    icon: 'bi-play-circle',
    title: 'Token-Simulation',
    content: (
      <>
        <p>
          Die <strong>Token-Simulation</strong> ermöglicht es, den Prozessfluss
          visuell zu testen, ohne den Prozess tatsächlich auszuführen.
        </p>

        <p><strong>Simulation starten:</strong></p>
        <ol>
          <li>
            Klicken Sie auf das <strong>Play-Symbol</strong> am
            unteren linken Rand des Editors (Toggle-Button, eingefügt durch das Token-Simulation-Plugin).
          </li>
          <li>
            Im Simulations-Modus erscheinen <strong>grüne Play-Buttons</strong> an allen Start-Events.
          </li>
          <li>
            Klicken Sie auf einen Play-Button — ein <strong>Token</strong> (animierte Kugel)
            wandert entlang der Sequenzflüsse durch den Prozess.
          </li>
        </ol>

        <p><strong>Gateways in der Simulation:</strong></p>
        <ul>
          <li><strong>Exclusive Gateway (XOR):</strong> Sie wählen manuell den ausgehenden Pfad.</li>
          <li><strong>Parallel Gateway (AND):</strong> Token wird automatisch auf alle Pfade verteilt.</li>
          <li><strong>Inclusive Gateway (OR):</strong> Wählen Sie einen oder mehrere Pfade.</li>
        </ul>

        <p><strong>Steuerung:</strong></p>
        <ul>
          <li><strong>Geschwindigkeit:</strong> Regelbar über die Steuerleiste der Simulation.</li>
          <li><strong>Pause / Fortsetzen:</strong> Simulation kann jederzeit angehalten werden.</li>
          <li><strong>Zurücksetzen:</strong> Setzt alle Token zurück zum Anfang.</li>
          <li><strong>Log:</strong> Zeigt den chronologischen Verlauf aller Token-Bewegungen.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Die Simulation funktioniert nur im Edit-Modus. Während der Simulation können
          keine Elemente verändert werden — deaktivieren Sie die Simulation zuerst.
        </div>
      </>
    ),
  },

  /* ───── 18. Layout & Arbeitstechniken ───── */
  {
    id: 'layout-tools',
    icon: 'bi-rulers',
    title: 'Layout & Arbeitstechniken',
    content: (
      <>
        <p>
          Bei großen Diagrammen helfen folgende Werkzeuge und Techniken:
        </p>

        <p><strong>Werkzeuge in der Palette:</strong></p>
        <ul>
          <li><strong>Space Tool</strong> (Taste <kbd>S</kbd>) – Klicken und ziehen Sie, um in einem
              Bereich Platz zu schaffen. Alle Elemente rechts/unterhalb werden verschoben.</li>
          <li><strong>Lasso-Auswahl</strong> (Taste <kbd>L</kbd>) – Ziehen Sie ein Rechteck um
              mehrere Elemente, um sie gemeinsam auszuwählen und zu verschieben.</li>
          <li><strong>Hand Tool</strong> (Taste <kbd>H</kbd>) – Verschieben der gesamten Zeichenfläche
              (alternativ: Leertaste + linke Maustaste).</li>
        </ul>

        <p><strong>Automatische Hilfen:</strong></p>
        <ul>
          <li><strong>Auto-Expand:</strong> Pools, Lanes und Sub-Prozesse erweitern sich automatisch,
              wenn Sie Elemente an den Rand oder darüber hinaus verschieben.</li>
          <li><strong>Snap &amp; Alignment:</strong> Beim Verschieben erscheinen blaue Hilfslinien,
              die Ihnen helfen, Elemente bündig auszurichten.</li>
          <li><strong>Auto-Connect:</strong> Ziehen Sie aus der Palette direkt auf ein bestehendes Element,
              um es automatisch zu verbinden.</li>
        </ul>

        <p><strong>Best Practices für das Layout:</strong></p>
        <ul>
          <li>Halten Sie eine klare <strong>Layout-Richtung</strong> ein (typisch: links → rechts).</li>
          <li>Bündeln Sie <strong>parallele Pfade horizontal</strong> und halten Sie Split/Join-Gateways symmetrisch.</li>
          <li>Nutzen Sie <strong>eingeklappte Sub-Prozesse</strong>, um Details zu verstecken und
              die Übersichtlichkeit zu wahren.</li>
          <li>Verwenden Sie <strong>Lanes</strong>, um Verantwortlichkeiten visuell zu trennen.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Nutzen Sie das Space Tool intensiv — es verschiebt ganze Bereiche auf einmal und
          spart viel manuelle Nacharbeit beim Wachsen des Diagramms.
        </div>
      </>
    ),
  },

  /* ───── 18b. Ausrichten & Verteilen ───── */
  {
    id: 'align',
    icon: 'bi-distribute-horizontal',
    title: 'Ausrichten & Verteilen',
    content: (
      <>
        <p><strong>Mehrfachauswahl:</strong></p>
        <ul>
          <li>Halten Sie <kbd>Ctrl</kbd> und klicken Sie auf Elemente, um mehrere gleichzeitig
              auszuwählen (zum Verschieben, Löschen oder Einfärben).</li>
          <li>Alternativ: Verwenden Sie die <strong>Lasso-Auswahl</strong> (<kbd>L</kbd>),
              um ein Rechteck um mehrere Elemente zu ziehen.</li>
        </ul>

        <p><strong>Ausrichten &amp; Verteilen:</strong></p>
        <p>
          In der Werkzeugleiste finden Sie den Button <strong>Ausrichten</strong> (aktiv ab 2 ausgewählten Elementen).
          Ein Klick öffnet ein Dropdown mit folgenden Optionen:
        </p>
        <ul>
          <li><strong>Ausrichten (ab 2 Elementen):</strong> <em>Links ausrichten</em>,
              <em>Horizontal zentrieren</em>, <em>Rechts ausrichten</em>, <em>Oben ausrichten</em>,
              <em>Vertikal zentrieren</em>, <em>Unten ausrichten</em> — richtet die ausgewählten
              Elemente an einer gemeinsamen Kante bzw. Achse aus.</li>
          <li><strong>Verteilen (ab 3 Elementen):</strong> <em>Horizontal verteilen</em> oder
              <em>Vertikal verteilen</em> — gleichmäßige Abstände. Mit nur zwei Elementen
              bleiben diese beiden Knöpfe grau.</li>
        </ul>

        <div className="help-tip">
          <i className="bi bi-lightbulb"></i>{' '}
          Nutzen Sie &quot;Ausrichten&quot; für schnelle Korrekturen an der Positionierung
          und &quot;Verteilen&quot; für gleichmäßige Abstände zwischen Elementen.
        </div>
      </>
    ),
  },

  /* ───── 19. Navigation & Zoom ───── */
  {
    id: 'navigation',
    icon: 'bi-arrows-move',
    title: 'Navigation & Zoom',
    content: (
      <>
        <p>Navigieren Sie das Diagramm mit folgenden Werkzeugen:</p>
        <ul>
          <li><strong>Zoom +/−:</strong> Buttons in der Werkzeugleiste oder Mausrad.</li>
          <li><strong>An Fläche anpassen:</strong> Klicken Sie auf den
              Fit-to-Screen-Button, um das gesamte Diagramm einzupassen.</li>
          <li><strong>Verschieben:</strong> Halten Sie die mittlere Maustaste oder{' '}
              <kbd>Leertaste</kbd> + linke Maustaste und ziehen Sie die Zeichenfläche.</li>
        </ul>

        <p><strong>Raster (Grid):</strong></p>
        <ul>
          <li>In der Werkzeugleiste befindet sich neben den Zoom-Buttons ein
              {' '}<strong>Raster-Button</strong> (<i className="bi bi-grid-3x3"></i>).</li>
          <li>Standardmäßig ist das Raster <strong>ausgeblendet</strong>. Per Klick wird ein
              gepunktetes <strong>Hintergrund-Raster</strong> auf der Zeichenfläche eingeblendet.</li>
          <li>Das Raster erleichtert die gleichmäßige Ausrichtung von Elementen
              und sorgt für ein sauberes Layout.</li>
          <li>Ein erneuter Klick blendet das Raster wieder aus.</li>
        </ul>

        <p><strong>Minimap:</strong></p>
        <ul>
          <li>Unten rechts im Editor befindet sich die <strong>Minimap</strong> — eine
              verkleinerte Übersicht des gesamten Diagramms.</li>
          <li>Klicken und ziehen Sie in der Minimap, um schnell zu einem bestimmten
              Bereich zu navigieren.</li>
          <li>Besonders nützlich bei großen Diagrammen mit vielen Pools und Sub-Prozessen.</li>
        </ul>
      </>
    ),
  },

  /* ───── 20. Tastenkürzel ───── */
  {
    id: 'shortcuts',
    icon: 'bi-keyboard',
    title: 'Tastenkürzel',
    content: (
      <table className="shortcut-table">
          <thead>
            <tr><th>Tastenkürzel</th><th>Aktion</th></tr>
          </thead>
          <tbody>
            <tr><td><kbd>Ctrl</kbd> + <kbd>Z</kbd></td><td>Rückgängig (Undo)</td></tr>
            <tr><td><kbd>Ctrl</kbd> + <kbd>Y</kbd></td><td>Wiederholen (Redo)</td></tr>
            <tr><td><kbd>Ctrl</kbd> + <kbd>C</kbd></td><td>Kopieren</td></tr>
            <tr><td><kbd>Ctrl</kbd> + <kbd>V</kbd></td><td>Einfügen</td></tr>
            <tr><td><kbd>Ctrl</kbd> + <kbd>A</kbd></td><td>Alles auswählen</td></tr>
            <tr><td><kbd>Ctrl</kbd> + <kbd>G</kbd></td><td>Auswahl in Sub-Prozess umwandeln</td></tr>
            <tr><td><kbd>Delete</kbd></td><td>Element löschen</td></tr>
            <tr><td><kbd>E</kbd></td><td>Element direkt umbenennen</td></tr>
            <tr><td><kbd>S</kbd></td><td>Space Tool aktivieren</td></tr>
            <tr><td><kbd>L</kbd></td><td>Lasso-Auswahl aktivieren</td></tr>
            <tr><td><kbd>H</kbd></td><td>Hand Tool (Verschieben)</td></tr>
            <tr><td><kbd>Mausrad</kbd></td><td>Zoom ein/aus</td></tr>
            <tr><td><kbd>Leertaste</kbd> + Ziehen</td><td>Zeichenfläche verschieben</td></tr>
            <tr><td><kbd>Ctrl</kbd> + Klick</td><td>Mehrfachauswahl</td></tr>
          </tbody>
        </table>
    ),
  },
];

// Pre-build search index (plain text per section, computed once)
const searchIndex = sections.map((s) => ({
  id: s.id,
  text: (s.title + ' ' + extractText(s.content)).toLowerCase(),
}));

const HelpPanel = ({ isOpen, onClose }) => {
  const [openSections, setOpenSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const isDragging = useRef(false);

  // --- Resize logic (drag left edge to change width) ---
  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!isDragging.current || !panelRef.current) return;
      const newWidth = window.innerWidth - ev.clientX;
      const clamped = Math.max(280, Math.min(800, newWidth));
      panelRef.current.style.width = clamped + 'px';
      panelRef.current.style.minWidth = clamped + 'px';
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // Focus search input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 150);
    }
  }, [isOpen]);

  // Reset search when panel closes
  useEffect(() => {
    if (!isOpen) setSearchQuery('');
  }, [isOpen]);

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Filter sections and auto-open matches
  const { filteredSections, matchIds } = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return { filteredSections: sections, matchIds: null };
    const words = q.split(/\s+/);
    const matched = [];
    const ids = new Set();
    for (const section of sections) {
      const idx = searchIndex.find((si) => si.id === section.id);
      const haystack = idx ? idx.text : section.title.toLowerCase();
      if (words.every((w) => haystack.includes(w))) {
        matched.push(section);
        ids.add(section.id);
      }
    }
    return { filteredSections: matched, matchIds: ids };
  }, [searchQuery]);

  // When searching, auto-open all matching sections
  const effectiveOpen = useMemo(() => {
    if (matchIds) {
      const obj = {};
      matchIds.forEach((id) => { obj[id] = true; });
      return obj;
    }
    return openSections;
  }, [matchIds, openSections]);

  const handleClearSearch = () => {
    setSearchQuery('');
    if (inputRef.current) inputRef.current.focus();
  };

  if (!isOpen) return null;

  const sectionCountLabel = filteredSections.length === 1 ? 'Abschnitt' : 'Abschnitte';

  return (
    <div className="help-panel" ref={panelRef}>
      <button
        type="button"
        className="help-panel-resize-handle"
        onMouseDown={onResizeMouseDown}
        aria-label="Panel-Breite anpassen"
      />
      <div className="help-panel-header">
        <span><i className="bi bi-book"></i> Benutzerhandbuch</span>
        <button className="btn-close-help" onClick={onClose} title="Schließen">
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
      <div className="help-search">
        <i className="bi bi-search help-search-icon"></i>
        <input
          ref={inputRef}
          type="text"
          className="help-search-input"
          placeholder="Handbuch durchsuchen…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="help-search-clear" onClick={handleClearSearch} title="Suche leeren">
            <i className="bi bi-x-circle-fill"></i>
          </button>
        )}
      </div>
      {searchQuery && (
        <div className="help-search-status">
          {filteredSections.length === 0
            ? 'Keine Treffer'
            : `${filteredSections.length} ${sectionCountLabel} gefunden`}
        </div>
      )}
      <div className="help-panel-body">
        {filteredSections.map((section) => (
          <div className="help-section" key={section.id}>
            <button
              className={`help-section-header ${effectiveOpen[section.id] ? 'open' : ''}`}
              onClick={() => toggleSection(section.id)}
            >
              <i className={`bi ${section.icon} section-icon`}></i>
              {section.title}
              <i className="bi bi-chevron-down chevron"></i>
            </button>
            {effectiveOpen[section.id] && (
              <div className="help-section-content">{section.content}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

HelpPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default HelpPanel;
